import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChartLine,
  faTableColumns,
  faRobot,
  faComments,
  faGraduationCap,
  faCog, 
  faSignOutAlt,
  faTint,
  faMountain,
  faGavel,
  faBoxes,
  faAddressBook,
  faUser,
  faUmbrellaBeach,
  faUsers,
  faLock,
  faUserShield,
  faChevronDown,
  faChevronRight,
  faChevronLeft,
  faAnglesRight,
  faAnglesLeft,
  faBell,
  faListCheck,
  faFolderOpen,
  faFileContract,
  faFileInvoiceDollar,
  faReceipt,
  faShoppingCart,
  faUndoAlt,
  faExclamationCircle,
  faBook,
  faStamp,
  faBuilding,
  faTags,
  faPercent,
  faDatabase,
  faSyncAlt,
  faSitemap,
  faUniversity,
  faAnchor,
  faChartColumn,
  faScaleBalanced,
  faMoon,
  faSun
} from '@fortawesome/free-solid-svg-icons';
import logo from '../../assets/images/IMT_logo.png';
import { getTheme, toggleTheme } from '../../theme/theme';
import OfflineNavItem from '../offline/OfflineNavItem';
import hmsAdminService from '../../services/hmsAdminService';
import notificationService from '../../services/notificationService';
import hmsColleaguesService from '../../services/hmsColleaguesService';
import axiosInstance from '../../utils/axiosConfig';
import { isRestrictedCustomerTrader } from '../../utils/customerTraderAccess';
import './HMSNavbar.css';

/**
 * HMS Navbar Component
 * Reusable navigation sidebar with icons, expandable menus, and responsive design
 * 
 * Props:
 * - isOpen: boolean - controls sidebar visibility (for mobile)
 * - onClose: function - callback when sidebar should close
 * - isCollapsed: boolean - controls collapsed/expanded state (for desktop)
 * - onToggleCollapse: function - callback to toggle collapse state
 * - className: string - additional CSS classes
 */
function HMSNavbar({ isOpen = false, onClose = () => {}, isCollapsed = false, onToggleCollapse = () => {}, className = '' }) {
  const history = useHistory();
  const location = useLocation();
  const [isSuperuser, setIsSuperuser] = useState(false);
  const [hasFullAccess, setHasFullAccess] = useState(false);
  const [hasCRMAccess, setHasCRMAccess] = useState(false);
  const [hasFinancialAccess, setHasFinancialAccess] = useState(false);
  // Template Headquarters is a cross-cutting cabinet (branding, deals, contracts,
  // invoicing, KYC) — gated separately from Financials so it can live at top level.
  const [hasCargoAccess, setHasCargoAccess] = useState(false);
  const [hasTemplateAccess, setHasTemplateAccess] = useState(false);
  // Reporting Central: the server's own `can_open_reporting_central` answer,
  // taken as given (it already covers superusers). Never re-derived from other
  // flags — that is how a link drifts from what the backend actually allows.
  const [hasReportingAccess, setHasReportingAccess] = useState(false);
  // Paper Pricing (ledger row 132): the server's own `can_open_paper_pricing`
  // answer (the Hedging Trader tick, or a superuser), taken as given -- same
  // reasoning as Reporting Central above.
  const [hasPaperPricingAccess, setHasPaperPricingAccess] = useState(false);
  // Bunkers operators (has_bunkers_access) get the Trade Ops Bunkers link
  // without unlocking the other full-access sub-nav items.
  const [hasBunkersAccess, setHasBunkersAccess] = useState(false);
  // Bunker Invoice Approvals link (2026-09-17): the Bunker Operations Approvals
  // group, plus superusers read-only. Server-computed, never re-derived here.
  const [canViewBunkerInvoiceApprovals, setCanViewBunkerInvoiceApprovals] = useState(false);
  // Legal is tracked SEPARATELY from bunkers access (2026-08-26). It used to
  // ride on hasBunkersAccess, so closing the module by editing that flag would
  // silently have taken Trade Ops Bunkers away from every bunkers operator too.
  const [hasLegalAccess, setHasLegalAccess] = useState(false);
  // Teams area is staff-wide EXCEPT customer traders — they hold module flags
  // (has_bunkers_access), so the link must key on is_customer_trader itself.
  // Superusers are exempt from the trader overlay (house convention, mirrored
  // by teams.permissions.is_external_trader): an admin account carrying the
  // flag is internal staff, not a customer.
  const [isCustomerTrader, setIsCustomerTrader] = useState(false);
  const [isDashboardExpanded, setIsDashboardExpanded] = useState(false);
  const [isFinancialsExpanded, setIsFinancialsExpanded] = useState(false);
  const [isInvoicingExpanded, setIsInvoicingExpanded] = useState(false);
  const [isMasterDataExpanded, setIsMasterDataExpanded] = useState(false);
  const [isSettingsExpanded, setIsSettingsExpanded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [pendingLeaveRequestsCount, setPendingLeaveRequestsCount] = useState(0);
  const [colleaguesCelebrationCount, setColleaguesCelebrationCount] = useState(0);
  const [theme, setThemeState] = useState(getTheme());

  // Keep the toggle label in sync if the theme changes elsewhere (e.g. a
  // second navbar instance or a future settings switch).
  useEffect(() => {
    const onThemeChanged = (e) => setThemeState(e.detail?.theme || getTheme());
    window.addEventListener('hmsThemeChanged', onThemeChanged);
    return () => window.removeEventListener('hmsThemeChanged', onThemeChanged);
  }, []);

  const handleThemeToggle = () => {
    setThemeState(toggleTheme());
  };

  const handleLogout = async () => {
    try {
      // Call backend logout to clear cookies
      await axiosInstance.post('/api/authentication/logout/');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear any local storage
      localStorage.removeItem('isLoggedIn');
      localStorage.removeItem('user_id');
      // Redirect to login
      history.push('/login');
    }
  };

  const isActive = (path) => {
    return location.pathname === path ? "active" : "";
  };

  const isDashboardActive = () => {
    // /dashboard itself is My Desk (its own top-level entry); the module
    // pages live underneath it.
    if (location.pathname === '/dashboard' || location.pathname.startsWith('/dashboard/my-desk')) return "";
    return location.pathname.startsWith('/dashboard') ? "active" : "";
  };

  const isSettingsActive = () => {
    return location.pathname.startsWith('/settings') ? "active" : "";
  };

  const isTrainingActive = () => {
    return location.pathname.startsWith('/training') ? "active" : "";
  };

  const isFinancialsActive = () => {
    return location.pathname.startsWith('/invoicing') ? "active" : "";
  };

  const toggleDashboard = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Only toggle submenu - don't navigate
    setIsDashboardExpanded(!isDashboardExpanded);
    setIsFinancialsExpanded(false);
    setIsSettingsExpanded(false);
  };

  const toggleFinancials = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const expanding = !isFinancialsExpanded;
    setIsFinancialsExpanded(expanding);
    if (!expanding) {
      setIsInvoicingExpanded(false);
      setIsMasterDataExpanded(false);
    }
    setIsDashboardExpanded(false);
    setIsSettingsExpanded(false);
  };

  const navigateToFinancials = (e) => {
    // Navigate to main financials dashboard -- except for the bunker invoice
    // approvals group without the Financials module (2026-09-17): that dashboard
    // refuses them, so the heading opens the one screen in here that is theirs.
    history.push(hasFinancialAccess ? '/invoicing/dashboard' : '/invoicing/bunker-approvals');
    
    // Expand submenu when navigating
    setIsFinancialsExpanded(true);
    setIsDashboardExpanded(false);
    setIsSettingsExpanded(false);
    
    // Close mobile menu if open
    onClose();
  };

  const navigateToDashboard = (e) => {
    // Navigate to main dashboard route
    history.push('/dashboard/modules');
    
    // Expand submenu when navigating
    setIsDashboardExpanded(true);
    setIsSettingsExpanded(false);
    
    // Close mobile menu if open
    onClose();
  };

  const toggleInvoicing = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsInvoicingExpanded(!isInvoicingExpanded);
  };

  const toggleMasterData = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsMasterDataExpanded(!isMasterDataExpanded);
  };

  const toggleSettings = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Only toggle submenu - don't navigate
    setIsSettingsExpanded(!isSettingsExpanded);
    setIsDashboardExpanded(false);
    setIsFinancialsExpanded(false);
  };

  const navigateToSettings = (e) => {
    // Navigate to main settings route (which redirects to account-information)
    history.push('/settings');
    
    // Expand submenu when navigating
    setIsSettingsExpanded(true);
    setIsDashboardExpanded(false);
    setIsFinancialsExpanded(false);
    
    // Close mobile menu if open
    onClose();
  };

  // Check superuser status and access level on component mount
  useEffect(() => {
    const checkUserPermissions = async () => {
      try {
        const response = await axiosInstance.get('/api/dashboard/user-info/');
        const userData = response.data;
        setIsSuperuser(userData.is_superuser || false);
        
        const fullAccess = userData.has_full_access || userData.is_superuser || userData.is_staff || false;
        setHasFullAccess(fullAccess);

        // Trade Ops Cargo opens to full-access users OR Cargo-scoped operators
        // (has_cargo_access). Mirrors the Bunkers gate below so a cargo-only
        // operator actually sees the sidebar link, not just the dashboard card.
        setHasCargoAccess(fullAccess || userData.has_cargo_access || false);

        setHasBunkersAccess(fullAccess || userData.has_bunkers_access || false);

        // Bunker Invoice Approvals (2026-09-17): the server's own answer, so the
        // link can never be offered to someone the screen would refuse.
        setCanViewBunkerInvoiceApprovals(Boolean(userData.can_view_bunker_invoice_approvals));

        // Legal module is MANAGEMENT-ONLY (Craig, 2026-08-27: "managers keep
        // full list"). Kept on its own flag rather than riding on
        // hasBunkersAccess, which also gates the Trade Ops Bunkers link.
        // Legal Approvers see the contracts sent to Legal for review.
        setHasLegalAccess(Boolean(
          fullAccess || userData.is_superuser || userData.is_staff || userData.is_legal_approver,
        ));

        // Financial access: either full access OR specific financial access OR financial approver
        const financialAccess = fullAccess || userData.has_financial_access || userData.is_financial_approver || false;
        setHasFinancialAccess(financialAccess);

        setHasCargoAccess(fullAccess || userData.has_cargo_access || false);
        setHasBunkersAccess(fullAccess || userData.has_bunkers_access || false);
        setHasTemplateAccess(
            fullAccess
            || userData.has_financial_access
            || userData.can_approve_templates
            || userData.is_legal_approver
            || userData.is_financial_approver
            || userData.is_kyc_approver
            || false,
        );

        // Reporting Central: the server's answer, unmodified.
        setHasReportingAccess(userData?.can_open_reporting_central === true);

        // Paper Pricing: the server's answer, unmodified.
        setHasPaperPricingAccess(userData?.can_open_paper_pricing === true);

        // CRM access: full access, CRM access, or financial access (invoicing depends on CRM data)
        const crmAccess = fullAccess || userData.has_crm_access || financialAccess || false;
        setHasCRMAccess(crmAccess);

        // Shared predicate (utils/customerTraderAccess.js). This check was
        // correct but was the ONLY copy carrying the superuser exemption —
        // FuelEnquiryForm's gate keyed on the raw flag and drifted for six
        // weeks. Both now read the same rule.
        setIsCustomerTrader(isRestrictedCustomerTrader(userData));
      } catch (error) {
        console.error('Error checking user permissions:', error);
        setIsSuperuser(false);
        setHasFullAccess(false);
        setHasCRMAccess(false);
        setHasFinancialAccess(false);
        setHasCargoAccess(false);
        setHasTemplateAccess(false);
        setHasBunkersAccess(false);
        setCanViewBunkerInvoiceApprovals(false);
        // Fail closed: if we can't confirm who this is, keep Teams hidden.
        setIsCustomerTrader(true);
      }
    };

    checkUserPermissions();
  }, []);

  // Fetch notification stats for badge count
  useEffect(() => {
    const fetchNotificationStats = async () => {
      try {
        const stats = await notificationService.getStats();
        setUnreadNotificationCount(stats.unread || 0);
      } catch (error) {
        console.error('Error fetching notification stats:', error);
        setUnreadNotificationCount(0);
      }
    };

    const fetchPendingLeaveRequests = async () => {
      try {
        // Only fetch if user is superuser
        if (!isSuperuser) {
          setPendingLeaveRequestsCount(0);
          return;
        }
        
        const response = await axiosInstance.get('/api/leave/admin/requests/?status=pending');
        const data = response.data;
        const leaveRequests = data.results || data;
        const pendingCount = leaveRequests.filter(req => req.status === 'pending').length;
        setPendingLeaveRequestsCount(pendingCount);
      } catch (error) {
        console.error('Error fetching pending leave requests:', error);
        setPendingLeaveRequestsCount(0);
      }
    };

    const fetchColleaguesStats = async () => {
      try {
        const stats = await hmsColleaguesService.getColleaguesStats();
        setColleaguesCelebrationCount(stats.total_today || 0);
      } catch (error) {
        console.error('Error fetching colleagues stats:', error);
        setColleaguesCelebrationCount(0);
      }
    };

    const fetchAllStats = () => {
      fetchNotificationStats();
      fetchPendingLeaveRequests();
      fetchColleaguesStats();
    };

    fetchAllStats();

    // Refresh counts every 30 seconds for better responsiveness
    const intervalId = setInterval(fetchAllStats, 30000);

    // Listen for immediate updates when notifications are marked read/unread
    const handleNotificationChange = () => {
      fetchAllStats();
    };
    
    window.addEventListener('notificationStatsChanged', handleNotificationChange);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('notificationStatsChanged', handleNotificationChange);
    };
  }, [isSuperuser]);


  // Auto-expand navigation based on current route
  useEffect(() => {
    if (location.pathname.startsWith('/dashboard')) {
      setIsDashboardExpanded(true);
      setIsSettingsExpanded(false);
      setIsFinancialsExpanded(false);
    } else if (location.pathname.startsWith('/invoicing')) {
      setIsFinancialsExpanded(true);
      setIsDashboardExpanded(false);
      setIsSettingsExpanded(false);
      if (location.pathname.startsWith('/invoicing/master-data')) {
        setIsMasterDataExpanded(true);
      }
      const invoiceRoutes = ['/invoicing/outgoing', '/invoicing/proforma', '/invoicing/incoming', '/invoicing/purchase-orders', '/invoicing/credit-notes', '/invoicing/debit-notes'];
      if (invoiceRoutes.some(r => location.pathname.startsWith(r))) {
        setIsInvoicingExpanded(true);
      }
    } else if (location.pathname.startsWith('/settings')) {
      setIsSettingsExpanded(true);
      setIsDashboardExpanded(false);
      setIsFinancialsExpanded(false);
    }
  }, [location.pathname]);

  // Hover-intent delay: only expand once the cursor rests on the rail,
  // so brushing past the collapsed sidebar doesn't cover the page content
  const hoverTimerRef = useRef(null);

  useEffect(() => {
    return () => clearTimeout(hoverTimerRef.current);
  }, []);

  const handleMouseEnter = () => {
    if (isCollapsed) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = setTimeout(() => setIsHovered(true), 300);
    }
  };

  const handleMouseLeave = () => {
    clearTimeout(hoverTimerRef.current);
    setIsHovered(false);
  };

  const isEffectivelyCollapsed = isCollapsed && !isHovered;

  return (
    <aside 
      className={`hms-navbar-sidebar ${isOpen ? 'open' : ''} ${isCollapsed && !isHovered ? 'collapsed' : ''} ${isHovered ? 'hover-expanded' : ''} ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="hms-navbar-logo-section">
        <img src={logo} alt="IMT" className="hms-navbar-logo" />
      </div>
      <nav className="hms-navbar-section">
        <ul className="hms-navbar-list">
          <li>
            <Link to="/dashboard" className={isActive("/dashboard")} onClick={onClose} title="My Workspace">
              <FontAwesomeIcon icon={faTableColumns} className="hms-nav-icon" />
              <span>My Workspace</span>
            </Link>
          </li>
          <li>
            <div 
              className={`hms-nav-parent ${isDashboardActive()}`} 
              title="IMT Dashboard"
            >
              <div 
                className="hms-nav-parent-content"
                onClick={navigateToDashboard}
              >
                <FontAwesomeIcon icon={faChartLine} className="hms-nav-icon" />
                <span>IMT Dashboard</span>
              </div>
              <FontAwesomeIcon 
                icon={isDashboardExpanded ? faChevronDown : faChevronRight} 
                className="hms-nav-chevron" 
                onClick={toggleDashboard}
              />
            </div>
            {!isEffectivelyCollapsed && <div className={`hms-subnav ${isDashboardExpanded ? 'visible' : ''}`}>
              {hasCargoAccess && (
                <Link to="/dashboard/trades/solids" className={isActive("/dashboard/trades/solids")} onClick={onClose}>
                  <FontAwesomeIcon icon={faMountain} className="hms-subnav-icon" />
                  Trade Ops Cargo
                </Link>
              )}
              {hasBunkersAccess && (
                <Link to="/dashboard/trades/fuels" className={isActive("/dashboard/trades/fuels")} onClick={onClose}>
                  <FontAwesomeIcon icon={faTint} className="hms-subnav-icon" />
                  Trade Ops Bunkers
                </Link>
              )}
              {hasLegalAccess && (
                <Link to="/dashboard/legal" className={isActive("/dashboard/legal")} onClick={onClose}>
                  <FontAwesomeIcon icon={faGavel} className="hms-subnav-icon" />
                  Legal
                </Link>
              )}
              {hasFullAccess && (
                <Link to="/dashboard/risk-analytics" className={isActive("/dashboard/risk-analytics")} onClick={onClose}>
                  <FontAwesomeIcon icon={faBoxes} className="hms-subnav-icon" />
                  Product Module
                </Link>
              )}
              {hasCRMAccess && (
                <Link to="/dashboard/crm" className={isActive("/dashboard/crm")} onClick={onClose}>
                  <FontAwesomeIcon icon={faAddressBook} className="hms-subnav-icon" />
                  IMT CRM
                </Link>
              )}
              {isSuperuser && (
                <Link to="/dashboard/corporate-structure" className={isActive("/dashboard/corporate-structure")} onClick={onClose}>
                  <FontAwesomeIcon icon={faSitemap} className="hms-subnav-icon" />
                  Schernikau Group
                </Link>
              )}
            </div>}
          </li>
          {/* TEMPORARILY DISABLED - October 17, 2025
              Reason: HAMSTIR feature temporarily hidden from users, to be reinstated later
              Backend functionality remains fully intact */}
          {/* <li>
            <Link to="/hmstr-ai" className={isActive("/hmstr-ai")} onClick={onClose} title="HAMSTIR AI">
              <FontAwesomeIcon icon={faRobot} className="hms-nav-icon" />
              <span>H<span className="hms-ai-highlight">A</span>MST<span className="hms-ai-highlight">I</span>R</span>
            </Link>
          </li> */}
          {/* Bunker Invoice Approvals lives in here (2026-09-17), so the group that
              checks bunker invoices opens Financials even without the Financials
              module. Everything else under it stays finance-only. */}
          {(hasFinancialAccess || canViewBunkerInvoiceApprovals) && (
            <li>
              <div
                className={`hms-nav-parent ${isFinancialsActive()}`}
                title="Financials"
              >
                <div 
                  className="hms-nav-parent-content"
                  onClick={navigateToFinancials}
                >
                  <FontAwesomeIcon icon={faFileInvoiceDollar} className="hms-nav-icon" />
                  <span>Financials</span>
                </div>
                <FontAwesomeIcon 
                  icon={isFinancialsExpanded ? faChevronDown : faChevronRight} 
                  className="hms-nav-chevron" 
                  onClick={toggleFinancials}
                />
              </div>
              {!isEffectivelyCollapsed && <div className={`hms-subnav ${isFinancialsExpanded ? 'visible' : ''}`}>
                <div
                  className={`hms-subnav-parent ${['/invoicing/bunker-approvals', '/invoicing/outgoing', '/invoicing/proforma', '/invoicing/incoming', '/invoicing/purchase-orders', '/invoicing/credit-notes', '/invoicing/debit-notes'].some(r => location.pathname.startsWith(r)) ? 'active' : ''}`}
                  onClick={toggleInvoicing}
                >
                  <div className="hms-subnav-parent-content">
                    <FontAwesomeIcon icon={faFileInvoiceDollar} className="hms-subnav-icon" />
                    <span>Invoicing</span>
                  </div>
                  <FontAwesomeIcon
                    icon={isInvoicingExpanded ? faChevronDown : faChevronRight}
                    className="hms-subnav-chevron"
                  />
                </div>
                <div className={`hms-subnav-nested ${isInvoicingExpanded ? 'visible' : ''}`}>
                  {/* First: the check that happens BEFORE any of these lists --
                      bunker delivery drafts on their way to finance. */}
                  {canViewBunkerInvoiceApprovals && (
                    <Link to="/invoicing/bunker-approvals" className={isActive("/invoicing/bunker-approvals")} onClick={onClose}>
                      <FontAwesomeIcon icon={faAnchor} className="hms-subnav-icon" />
                      Bunker Invoice Approvals
                    </Link>
                  )}
                  {hasFinancialAccess && (<>
                  <Link to="/invoicing/outgoing" className={isActive("/invoicing/outgoing")} onClick={onClose}>
                    <FontAwesomeIcon icon={faFileInvoiceDollar} className="hms-subnav-icon" />
                    Outgoing Invoices
                  </Link>
                  <Link to="/invoicing/proforma" className={isActive("/invoicing/proforma")} onClick={onClose}>
                    <FontAwesomeIcon icon={faFileContract} className="hms-subnav-icon" />
                    Pro-forma Invoices
                  </Link>
                  <Link to="/invoicing/incoming" className={isActive("/invoicing/incoming")} onClick={onClose}>
                    <FontAwesomeIcon icon={faReceipt} className="hms-subnav-icon" />
                    Incoming Invoices
                  </Link>
                  <Link to="/invoicing/purchase-orders" className={isActive("/invoicing/purchase-orders")} onClick={onClose}>
                    <FontAwesomeIcon icon={faShoppingCart} className="hms-subnav-icon" />
                    Purchase Orders
                  </Link>
                  <Link to="/invoicing/credit-notes" className={isActive("/invoicing/credit-notes")} onClick={onClose}>
                    <FontAwesomeIcon icon={faUndoAlt} className="hms-subnav-icon" />
                    Credit Notes
                  </Link>
                  <Link to="/invoicing/debit-notes" className={isActive("/invoicing/debit-notes")} onClick={onClose}>
                    <FontAwesomeIcon icon={faExclamationCircle} className="hms-subnav-icon" />
                    Debit Notes
                  </Link>
                  </>)}
                </div>
                {hasFinancialAccess && (<>
                <div
                  className={`hms-subnav-parent ${location.pathname.startsWith('/invoicing/master-data') ? 'active' : ''}`}
                  onClick={toggleMasterData}
                >
                  <div className="hms-subnav-parent-content">
                    <FontAwesomeIcon icon={faDatabase} className="hms-subnav-icon" />
                    <span>Finance Master Data</span>
                  </div>
                  <FontAwesomeIcon
                    icon={isMasterDataExpanded ? faChevronDown : faChevronRight}
                    className="hms-subnav-chevron"
                  />
                </div>
                <div className={`hms-subnav-nested ${isMasterDataExpanded ? 'visible' : ''}`}>
                  <Link to="/invoicing/master-data/gl-accounts" className={isActive("/invoicing/master-data/gl-accounts")} onClick={onClose}>
                    <FontAwesomeIcon icon={faBook} className="hms-subnav-icon" />
                    GL Accounts
                  </Link>
                  <Link to="/invoicing/master-data/cost-centres" className={isActive("/invoicing/master-data/cost-centres")} onClick={onClose}>
                    <FontAwesomeIcon icon={faBuilding} className="hms-subnav-icon" />
                    Cost Centres
                  </Link>
                  <Link to="/invoicing/master-data/cost-categories" className={isActive("/invoicing/master-data/cost-categories")} onClick={onClose}>
                    <FontAwesomeIcon icon={faTags} className="hms-subnav-icon" />
                    Cost Categories
                  </Link>
                  <Link to="/invoicing/master-data/vat-rates" className={isActive("/invoicing/master-data/vat-rates")} onClick={onClose}>
                    <FontAwesomeIcon icon={faPercent} className="hms-subnav-icon" />
                    VAT Rates
                  </Link>
                  <Link to="/invoicing/master-data/closed-months" className={isActive("/invoicing/master-data/closed-months")} onClick={onClose}>
                    <FontAwesomeIcon icon={faLock} className="hms-subnav-icon" />
                    Closed Months
                  </Link>
                  <Link to="/invoicing/abacus-sync" className={isActive("/invoicing/abacus-sync")} onClick={onClose}>
                    <FontAwesomeIcon icon={faSyncAlt} className="hms-subnav-icon" />
                    Abacus ERP Sync
                  </Link>
                </div>
                <Link to="/invoicing/bank-reconciliation" className={isActive("/invoicing/bank-reconciliation")} onClick={onClose}>
                  <FontAwesomeIcon icon={faUniversity} className="hms-subnav-icon" />
                  Bank Reconciliation
                </Link>
                </>)}
              </div>}
            </li>
          )}
          {hasTemplateAccess && (
            <li>
              <Link to="/template-hub" className={isActive("/template-hub")} onClick={onClose} title="Template HQ">
                <FontAwesomeIcon icon={faStamp} className="hms-nav-icon" />
                <span>Template HQ</span>
              </Link>
            </li>
          )}
          {hasReportingAccess && (
            <li>
              <Link to="/reporting" className={isActive("/reporting")} onClick={onClose} title="Reporting Central">
                <FontAwesomeIcon icon={faChartColumn} className="hms-nav-icon" />
                <span>Reporting Central</span>
              </Link>
            </li>
          )}
          {hasPaperPricingAccess && (
            <li>
              <Link to="/dashboard/paper-pricing" className={isActive("/dashboard/paper-pricing")} onClick={onClose} title="Paper Pricing">
                <FontAwesomeIcon icon={faScaleBalanced} className="hms-nav-icon" />
                <span>Paper Pricing</span>
              </Link>
            </li>
          )}
          <li>
            <Link to="/knowledge-hub" className={isActive("/knowledge-hub")} onClick={onClose} title="Knowledge Hub">
              <FontAwesomeIcon icon={faBook} className="hms-nav-icon" />
              <span>Knowledge Hub</span>
            </Link>
          </li>
          <li>
            <Link to="/training" className={isTrainingActive()} onClick={onClose} title="Hamster Training">
              <FontAwesomeIcon icon={faGraduationCap} className="hms-nav-icon" />
              <span>Hamster Training</span>
            </Link>
          </li>
          {/* Teams area — internal chat spaces + shared team todos. Staff-wide,
              hidden from customer traders (backend 403s them regardless). No
              live badge here on purpose: unread polling runs only inside the
              Teams screen (anti-hammer decision on teams-area task). */}
          {!isCustomerTrader && (
            <li>
              <Link to="/dashboard/teams" className={isActive("/dashboard/teams")} onClick={onClose} title="Teams">
                <FontAwesomeIcon icon={faComments} className="hms-nav-icon" />
                <span>Teams</span>
              </Link>
            </li>
          )}
          <li>
            <div 
              className={`hms-nav-parent ${isSettingsActive()}`} 
              title="Hamster HR"
            >
              <div 
                className="hms-nav-parent-content"
                onClick={navigateToSettings}
              >
                <FontAwesomeIcon icon={faCog} className="hms-nav-icon" />
                <span>Hamster HR</span>
              </div>
              <div className="hms-nav-chevron-container">
                <FontAwesomeIcon 
                  icon={isSettingsExpanded ? faChevronDown : faChevronRight} 
                  className="hms-nav-chevron" 
                  onClick={toggleSettings}
                />
                {(unreadNotificationCount > 0 || colleaguesCelebrationCount > 0) && (
                  <span className="hms-chevron-notification-badge">{unreadNotificationCount + colleaguesCelebrationCount}</span>
                )}
              </div>
            </div>
            {!isEffectivelyCollapsed && <div className={`hms-subnav ${isSettingsExpanded ? 'visible' : ''}`}>
              <Link to="/settings/account-information" className={isActive("/settings/account-information")} onClick={onClose}>
                <FontAwesomeIcon icon={faUser} className="hms-subnav-icon" />
                Account Information
              </Link>
              <Link to="/settings/my-files" className={isActive("/settings/my-files")} onClick={onClose}>
                <FontAwesomeIcon icon={faFolderOpen} className="hms-subnav-icon" />
                My Files
              </Link>
              <Link to="/settings/leave-management" className={isActive("/settings/leave-management")} onClick={onClose}>
                <FontAwesomeIcon icon={faUmbrellaBeach} className="hms-subnav-icon" />
                Leave Management
              </Link>
              <Link to="/settings/hms-colleagues" className={`${isActive("/settings/hms-colleagues")} hms-nav-link-with-badge`} onClick={onClose}>
                <FontAwesomeIcon icon={faUsers} className="hms-subnav-icon" />
                <span>IMT Colleagues</span>
                {colleaguesCelebrationCount > 0 && (
                  <span className="hms-nav-notification-badge">{colleaguesCelebrationCount}</span>
                )}
              </Link>
              <Link to="/settings/notifications" className={`${isActive("/settings/notifications")} hms-nav-link-with-badge`} onClick={onClose}>
                <FontAwesomeIcon icon={faBell} className="hms-subnav-icon" />
                <span>Notifications</span>
                {unreadNotificationCount > 0 && (
                  <span className="hms-nav-notification-badge">{unreadNotificationCount}</span>
                )}
              </Link>
              <Link to="/settings/todos" className={isActive("/settings/todos")} onClick={onClose}>
                <FontAwesomeIcon icon={faListCheck} className="hms-subnav-icon" />
                To-Do List
              </Link>
              <Link to="/settings/password-security" className={isActive("/settings/password-security")} onClick={onClose}>
                <FontAwesomeIcon icon={faLock} className="hms-subnav-icon" />
                Password Security
              </Link>
              {isSuperuser && (
                <Link to="/settings/hms-admin" className={isActive("/settings/hms-admin")} onClick={onClose}>
                  <FontAwesomeIcon icon={faUserShield} className="hms-subnav-icon" />
                  IMT Admin Dashboard
                </Link>
              )}
            </div>}
          </li>
          {/* Offline access. Lives in the navbar as well as on the dashboard
              because the realistic moment someone remembers is at the gate,
              already deep inside a recap — not on the landing page. */}
          <OfflineNavItem collapsed={isEffectivelyCollapsed} onNavigate={onClose} />
          <li className="hms-logout-item">
            <Link to="/login" onClick={() => { onClose(); handleLogout(); }} title="Logout">
              <FontAwesomeIcon icon={faSignOutAlt} className="hms-nav-icon" />
              <span>Logout</span>
            </Link>
          </li>
          <li className="hms-theme-row">
            <button
              type="button"
              className={`hms-theme-switch ${theme === 'dark' ? 'is-dark' : ''}`}
              onClick={handleThemeToggle}
              role="switch"
              aria-checked={theme === 'dark'}
              title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              <FontAwesomeIcon icon={theme === 'dark' ? faSun : faMoon} className="hms-nav-icon" />
              <span className="hms-theme-switch-label">Dark Mode</span>
              <span className="hms-theme-switch-track">
                <span className="hms-theme-switch-knob" />
              </span>
            </button>
          </li>
        </ul>
      </nav>
    </aside>
  );
}

export default HMSNavbar;

